import type { ApiAreaOfFocus } from 'api/types/ApiAreaOfFocus';
import type {
  ApiISODateString,
  ApiISODateTimeNoTzString,
} from 'api/types/ApiTypedDate';
import {
  FormattedDate,
  FormattedDateTime,
  SingleDayDateTime,
} from 'components/Date/DateUtil';
import { DetailsList } from 'components/DetailsList/DetailsList';
import { DetailsListItem } from 'components/DetailsList/Item/DetailsListItem';
import { DetailListSection } from 'components/DetailsList/Section/DetailListSection';
import { AREAS_OF_FOCUS } from 'utils/constants/general/areasOfFocus';

export const formatAmountString = (amount: string) => {
  const parts = amount.split('.');
  const cents = parseInt(parts[1], 10);
  const [dollars] = parts;
  return `$${dollars}${cents > 0 ? `.${cents}` : ''}`;
};

type Props = {
  areasOfFocus: ApiAreaOfFocus[];
  startDate: ApiISODateString | null;
  endDate: ApiISODateString | null;
  startsLocal: ApiISODateTimeNoTzString | null;
  endsLocal: ApiISODateTimeNoTzString | null;
  feeAmount: string | null;
  feeRequired: boolean;
  timezone: string | null;
  welcomeGroups: boolean;
  welcomeFamilies: boolean;
  detailsWheelchairAccessible: boolean;
};

export function EventDetailsList({
  areasOfFocus,
  startDate,
  endDate,
  startsLocal,
  endsLocal,
  feeAmount,
  feeRequired,
  timezone,
  welcomeGroups,
  welcomeFamilies,
  detailsWheelchairAccessible,
}: Props) {
  return (
    <DetailsList>
      {startDate && endDate && startDate === endDate && (
        <DetailsListItem
          title={getText('Date')}
          iconName="calendar"
          value={
            <SingleDayDateTime
              startDate={startsLocal ?? startDate}
              endDate={endsLocal ?? endDate}
              showStartTime={Boolean(startsLocal)}
              showEndTime={Boolean(endsLocal)}
              timezone={timezone}
            />
          }
        />
      )}

      {startDate && startDate !== endDate && (
        <DetailsListItem
          title={getText('Start Date')}
          iconName="hourglass"
          value={
            startsLocal ? (
              <FormattedDateTime dateTime={startsLocal} timezone={timezone} />
            ) : (
              <FormattedDate date={startDate} />
            )
          }
        />
      )}

      {endDate && endDate !== startDate && (
        <DetailsListItem
          title={getText('End Date')}
          iconName="hourglass"
          value={
            endsLocal ? (
              <FormattedDateTime dateTime={endsLocal} timezone={timezone} />
            ) : (
              <FormattedDate date={endDate} />
            )
          }
        />
      )}

      {feeRequired && (
        <DetailsListItem
          title={getText('Cost')}
          iconName="coin"
          value={`${getText(
            'Paid Event',
          )}${feeAmount ? `: ${formatAmountString(feeAmount)}` : ''}`}
        />
      )}

      {areasOfFocus?.length !== 0 && (
        <DetailsListItem
          iconName="globe"
          title={getText('Cause Areas')}
          value={areasOfFocus.map((item) => AREAS_OF_FOCUS[item]).join(', ')}
        />
      )}

      <DetailListSection title={getText('Participation')}>
        {welcomeGroups && (
          <DetailsListItem iconName="group" value={getText('Groups Welcome')} />
        )}

        {welcomeFamilies && (
          <DetailsListItem
            iconName="family"
            value={getText('Families Welcome')}
          />
        )}

        {detailsWheelchairAccessible && (
          <DetailsListItem
            iconName="wheelchair"
            value={getText('Wheelchair Accessible')}
          />
        )}
      </DetailListSection>
    </DetailsList>
  );
}
